import type { Meeting } from "./model";

/** Pure helpers for the meeting notes view (WP-017). */
export type MeetingNotes = Meeting["notes"];
export type ActionItem = MeetingNotes["actionItems"][number];

export interface Member {
  uid: string;
  name: string | null;
  email: string | null;
}

/** An action item as a task to create: editable before it is created. */
export interface TaskDraft {
  title: string;
  /** "" = nobody. */
  assigneeUid: string;
  /** `YYYY-MM-DD` or "". */
  due: string;
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

/**
 * The member a speaker's display name points to, only when exactly one member matches: the same
 * full name, else the same first name, else the address's local part. Ambiguous or unknown → "".
 */
export function matchAssignee(ownerName: string | null, members: readonly Member[]): string {
  if (ownerName === null) return "";
  const wanted = norm(ownerName);
  if (wanted.length === 0) return "";
  const first = wanted.split(" ")[0];
  const tries: Array<(m: Member) => boolean> = [
    (m) => m.name !== null && norm(m.name) === wanted,
    (m) => m.name !== null && first !== undefined && norm(m.name).split(" ")[0] === first,
    (m) => m.email !== null && norm(m.email.split("@")[0] ?? "") === wanted.replace(/ /g, "."),
  ];
  for (const test of tries) {
    const found = members.filter(test);
    if (found.length === 1 && found[0] !== undefined) return found[0].uid;
    if (found.length > 1) return "";
  }
  return "";
}

export function draftsFor(items: readonly ActionItem[], members: readonly Member[]): TaskDraft[] {
  return items.map((i) => ({ title: i.text.slice(0, 300), assigneeUid: matchAssignee(i.ownerName, members), due: i.due === null ? "" : i.due }));
}

/** A sentence for notes that are not ready. */
export function notesStatusText(notes: Pick<MeetingNotes, "status" | "detail">): string {
  switch (notes.status) {
    case "ready":
      return "";
    case "pending":
      return "Notes will appear here after the meeting, once Google has the transcript.";
    case "ai_disabled":
      return "AI is off for this workspace, so no notes were made.";
    case "failed":
      return "Notes could not be written for this meeting.";
    case "unavailable":
      return "No transcript was made for this meeting, so there are no notes. Meet transcription must be on (it needs a Workspace edition that includes it).";
    case "none":
      return notes.detail === "notes_scope_missing"
        ? "Notes are not on for this meeting: allow Meet again for the account in Settings → Accounts to include meeting notes."
        : "Notes are not collected for this meeting.";
  }
}

/** Whether the meeting has anything to show beyond its link. */
export function hasNotesView(notes: Pick<MeetingNotes, "status">): boolean {
  return notes.status !== "none";
}
